import { Controller } from "@hotwired/stimulus"

// Attached to the handbook article body. When the page is opened with a #fragment
// — from a search result or the on-page table of contents — this briefly
// highlights the target heading so the reader can see WHERE they landed. Without
// it a deep link drops you mid-document with no cue that the jump was deliberate.
//
// The scroll itself is mostly the browser's and Turbo's job; the call here is the
// fallback for the cases they skip (restoration visits, a repeat click on the
// fragment already in the URL).
const FLASH_MS = 1700

export default class extends Controller {
  connect() {
    // The ToC links are same-page fragments, so no navigation fires — only
    // hashchange tells us the reader jumped somewhere new.
    this.onHashChange = () => this.reveal()
    window.addEventListener("hashchange", this.onHashChange)
    this.reveal()
  }

  disconnect() {
    window.removeEventListener("hashchange", this.onHashChange)
    clearTimeout(this.timeout)
  }

  reveal() {
    // Anchors are German slugs ("export-durchführen"), so the hash arrives
    // percent-encoded and has to be decoded before it matches an id.
    const hash = window.location.hash.slice(1)
    if (!hash) return

    let id
    try {
      id = decodeURIComponent(hash)
    } catch {
      id = hash
    }

    const target = this.element.querySelector(`[id="${CSS.escape(id)}"]`)
    if (!target) return

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" })

    // The id may sit on the heading itself or on the zero-size <a class="anchor">
    // inside it, depending on the commonmarker version that rendered the corpus.
    // Climbing to the heading covers both — highlighting the anchor alone would
    // show nothing, since it has no width or height.
    const heading = target.closest("h1, h2, h3, h4, h5, h6") || target

    // Re-adding the class alone will not restart a running animation; the class
    // has to be dropped and the style recomputed first, or a second click on the
    // same ToC entry flashes nothing.
    heading.classList.remove("handbook-flash")
    void heading.offsetWidth
    heading.classList.add("handbook-flash")

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => heading.classList.remove("handbook-flash"), FLASH_MS)
  }
}
